import { Col } from "@amzn/stencil-react-components/layout";
import { Link } from "@amzn/stencil-react-components/link";
import React, { FC } from "react";
import { getString } from "@/common/uistringlabels/ui-string-utils";
import { PLATFORMS } from "@/services/device-options/device-options.types";
import { StepError, StepSpinner } from "@/pages/Replacement/components/step-status";
import { useReplacedDevice, PassedDeviceContext } from "@/pages/Replacement/hooks/use-replaced-device.hook";
import { NO_KEYBOARD_LAYOUT } from "@/pages/Replacement/replacement.types";
import { useReplacementDraftStore } from "@/pages/Replacement/use-replacement-draft-store";
import { useDeviceOptions } from "./hooks/use-device-options.hook";
import { AssetNotFound } from "./components/asset-not-found";
import { DeviceCard } from "./components/device-card";
import { DeviceOptionsEmpty } from "./components/device-options-empty";
import { KeyboardLayoutSelect } from "./components/keyboard-layout-select";
import { PlatformTabs } from "./components/platform-tabs";

const DEVICE_RADIO_GROUP = "replacement-device";

export interface ChooseDeviceStepProps {
  assetId: string;
  passedContext?: PassedDeviceContext;
  policyUrl: string;
  supportTicketHref: string;
}

export const ChooseDeviceStep: FC<ChooseDeviceStepProps> = ({
  assetId,
  passedContext,
  policyUrl,
  supportTicketHref,
}) => {
  const { device, status: replacedStatus, refetch: refetchDevice } = useReplacedDevice(
    assetId,
    passedContext
  );
  const category = device?.category ?? "";
  const { options, keyboardLayouts, isLoading, isError, isEmpty, refetch } = useDeviceOptions({
    assetId: device?.assetId ?? assetId,
    category,
  });

  const { platform, deviceId, keyboardLayout, selectPlatform, chooseDevice, setKeyboardLayout } =
    useReplacementDraftStore();

  if (replacedStatus === "loading") return <StepSpinner testId="choose-device-loading" />;
  if (replacedStatus === "not-found") return <AssetNotFound />;
  if (replacedStatus === "error") {
    return (
      <StepError
        testId="choose-device-error"
        message={getString("replacement.chooseDevice.deviceLoadError")}
        onRetry={() => refetchDevice()}
      />
    );
  }

  if (isLoading) return <StepSpinner testId="choose-device-loading" />;
  if (isError) {
    return (
      <StepError
        testId="choose-device-error"
        message={getString("replacement.chooseDevice.optionsLoadError")}
        onRetry={() => refetch()}
      />
    );
  }
  if (isEmpty) return <DeviceOptionsEmpty supportTicketHref={supportTicketHref} />;

  const platformsWithOptions = PLATFORMS.filter((candidate) =>
    options.some((option) => option.platform === candidate)
  );
  const activePlatform =
    platform && platformsWithOptions.includes(platform) ? platform : platformsWithOptions[0];
  const visibleOptions = options.filter((option) => option.platform === activePlatform);
  const selectedOption = options.find((option) => option.id === deviceId);

  return (
    <Col gridGap="S400" width="100%">
      <PlatformTabs activePlatform={activePlatform} onSelect={selectPlatform} />

      <Col
        gridGap="S200"
        width="100%"
        role="radiogroup"
        aria-label={getString("replacement.chooseDevice.eligibleModelsLabel")}
      >
        {visibleOptions.map((option) => (
          <DeviceCard
            key={option.id}
            option={option}
            selected={deviceId === option.id}
            groupName={DEVICE_RADIO_GROUP}
            onSelect={() => chooseDevice(option.id, keyboardLayouts)}
          />
        ))}
      </Col>

      <KeyboardLayoutSelect
        layouts={selectedOption ? keyboardLayouts : []}
        value={keyboardLayout === NO_KEYBOARD_LAYOUT ? undefined : keyboardLayout}
        onChange={setKeyboardLayout}
      />

      <Link href={policyUrl} target="_blank" rel="noopener noreferrer">
        {getString("replacement.chooseDevice.policyLink")}
      </Link>
    </Col>
  );
};
